import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { NomeDescrizione } from "../../models/StatoTipologiaModel";
import {
  fetchNomeDescrizioneForEntity,
  tipologiaBeneUri,
} from "../../api/statotipologia";
import {
  FetchPaginatedApiModel,
  WithAnagraficaClienteID,
  WithFabObj,
  WithData,
  WithRowOperations,
} from "../../models/shared/CommonModels";
import {
  fetchBeniQueryKey,
  fetchTipologiaBeneSelectQueryKey,
} from "../../utils/QueryClient";
import { deleteBene, fetchSelectBeni } from "../../api/beni";
import { BeneModelDetailed } from "../../models/BeneModel";
import {
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
} from "@mui/x-data-grid";
import FilteredDataTable from "../shared/FilteredDataTable";
import { castToBackendSort } from "../../utils/sorting";
import { SortingModel } from "../../models/shared/SortingModel";
import DeleteDialog from "../shared/DeleteDialog";
import BeneFormComponent from "./BeneFormComponent";
import { Box, Typography, Stack, Tooltip } from "@mui/material";
import ThumbnailFoto from "../foto/ThumbnailFoto";

interface BeneTableComponentProps extends WithAnagraficaClienteID, WithFabObj {
  dettaglio: boolean;
  enableCreate: boolean;
  toolbarActions?: React.ReactNode;
}

const BeneTableComponent: React.FC<BeneTableComponentProps> = ({
  anagrafica_cliente_id,
  fabVisible,
  alterFabVisibility,
  enableCreate,
  toolbarActions,
}) => {
  const navigator = useNavigate();
  const queryClient = useQueryClient();

  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const toggleDeleteAlert = () => setDeleteAlertOpen(!deleteAlertOpen);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 5,
  });
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const [beni, setBeni] = useState<FetchPaginatedApiModel<BeneModelDetailed[]>>(
    undefined as any
  );
  const [selectedRowElement, setSelectedRowElement] =
    useState<BeneModelDetailed>();

  const [filters, setFilters] = useState<GridFilterModel | undefined>();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [tipologiaBeneRecords, setTipologiaBeneRecords] = useState<
    NomeDescrizione[]
  >([]);

  const [errorMessage, setErrorMessage] = useState("");
  const hideError = () => setErrorMessage("");

  const condition2Color = [
    { id: "Valutazione", color: "#ffc107" },
    { id: "Approvato", color: "#4cafd3" },
    { id: "Restituito", color: "#607d8b" },
    { id: "Caricato", color: "#9e9e9e" },
    { id: "Venduto Direttamente", color: "#2196f3" },
    { id: "Reso", color: "#f44336" },
    { id: "Venduto", color: "#388e3c" },
    { id: "In Approvazione", color: "#ff9800" },
  ];

  const colorMap = new Map();
  condition2Color.forEach((data) => {
    colorMap.set(data.id, data.color);
  });

  // tipologia bene query
  useQuery({
    queryKey: [fetchTipologiaBeneSelectQueryKey],
    queryFn: () => {
      return fetchNomeDescrizioneForEntity(tipologiaBeneUri);
    },
    onSuccess: (getResponse) => {
      if (tipologiaBeneRecords.length <= 0) {
        setTipologiaBeneRecords(getResponse.data);
      }
    },
    refetchOnWindowFocus: false,
  });

  // query
  useQuery(
    [fetchBeniQueryKey, paginationModel.page, filters, sortBy, sortDirection],
    () => {
      const sor = {} as SortingModel;
      sor.field = sortBy || "id";
      sor.direction = sortDirection || "DESC";
      return fetchSelectBeni(
        paginationModel.page,
        filters,
        sor,
        anagrafica_cliente_id
      );
    },
    {
      keepPreviousData: true,
      onSuccess: (getResponse) => {
        setBeni(getResponse.data);
      },
      refetchOnWindowFocus: false,
    }
  );

  const deleteMutation = useMutation({
    mutationFn: (x: WithData) => deleteBene(x),
    onSuccess: () => {
      queryClient.invalidateQueries(fetchBeniQueryKey);
      queryClient.invalidateQueries(['beniStats']);
    },
    onError: (e: any) => {
      if (e.response.data.message !== undefined)
        setErrorMessage(e.response.data.message);
      else
        setErrorMessage(
          "Errore non previsto, riprovare o richiedere assistenza"
        );
    },
  });

  const detailsOperations = (
    record: GridRenderCellParams<any, BeneModelDetailed>
  ) => {
    if (record.id !== undefined) {
      let model = (record as any).actions as BeneModelDetailed;
      navigator("/benenew/".concat(model.id.toString()));
    }
  };

  const deleteOperations = (
    record: GridRenderCellParams<any, BeneModelDetailed>
  ) => {
    setSelectedRowElement(record.value);
    toggleDeleteAlert();
  };

  const columns: GridColDef[] = [
    {
      field: "foto_principale_url",
      headerName: "Foto",
      sortable: false,
      width: 70,
      renderCell: (params) => {
        // Uso params.id che è l'id della riga (che corrisponde a d.id del bene)
        const beneId = params.id;
        return (
          <ThumbnailFoto 
            url={params.value} 
            size={50} 
            onDoubleClickPlaceholder={() => {
              navigator(`/benenew/${beneId}?openUpload=true`);
            }}
          />
        );
      },
    },
    {
      field: "id",
      headerName: "ID Bene",
      flex: 1,
    },
    {
      field: "ubicazione",
      headerName: "Ubicazione",
      flex: 1,
    },
    {
      field: "sotto_tipologia_bene",
      headerName: "Sotto-Categoria",
      sortable: false,
      flex: 1,
    },
    {
      field: "qualita_bene",
      headerName: "Stato",
      sortable: false,
      flex: 1,
    },
    {
      field: "brand_bene",
      headerName: "Brand",
      sortable: false,
      flex: 1,
    },
    {
      field: "bene_contratto_titolo",
      headerName: "Contratto",
      sortable: false,
      flex: 2,
    },
    {
      field: "bene_condizione_nome",
      headerName: "Condizione",
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        const statusValue = params.value;
        return (
          <p style={{ color: colorMap.get(statusValue) }}>{statusValue}</p>
        );
      },
    },
    {
      field: "valutazione_min",
      headerName: "Min Valutazione",
      sortable: false,
      flex: 1,
    },
    {
      field: "valutazione_max",
      headerName: "Max Valutazione",
      sortable: false,
      flex: 1,
    },
    {
      field: "data_vendita",
      headerName: "Data Vendita",
      sortable: false,
      width: 110,
    },
    {
      field: "sale_price_gross",
      headerName: "Prz. Vend.",
      sortable: false,
      width: 100,
    },
    {
      field: "sale_price_net",
      headerName: "Prz. Netto",
      sortable: false,
      width: 100,
    },
    {
      field: "selling_notes",
      headerName: "Note di Vendita",
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        const fullText = params.value as string;
        const maxLength = 20;

        const truncatedText =
          fullText && fullText.length > maxLength
            ? `${fullText.substring(0, maxLength)} ...`
            : fullText;

        return (
          // Tooltip will show the full text on hover
          <Tooltip title={fullText || ""} enterDelay={500}>
            <Box
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%",
              }}
            >
              {truncatedText || "-"}
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <FilteredDataTable<BeneModelDetailed, WithRowOperations>
        loading={beni === undefined}
        detailsOperations={detailsOperations}
        deleteOperations={deleteOperations}
        toolbarActions={toolbarActions}
        rows={
          beni?.data !== undefined
            ? beni.data.map((d) => {
                return {
                  id: d.id,
                  foto_principale_url: d.benecontratto[0]?.foto_principale_url,
                  ubicazione: d.ubicazionebene?.nome || "-",
                  tipologia_bene: d.tipologiabene.nome,
                  sotto_tipologia_bene:
                    d.sottotipologiabene?.nome || "Non disponibile",
                  qualita_bene: d.qualitabene.nome,
                  brand_bene: d.brandbene?.nome || "Non disponibile",
                  bene_contratto_titolo:
                    d.benecontratto[0].contratto?.titolo || "Non associato",
                  bene_condizione_id: d.benecontratto[0].condizione.id,
                  bene_condizione_nome: d.benecontratto[0].condizione.nome,
                  valutazione_min: d.valutazione_minima + "€",
                  valutazione_max: d.valutazione_massima + "€",
                  data_vendita:
                    d.benecontratto?.[0]?.venditabenecontratto?.data_vendita
                      ? new Date(d.benecontratto[0].venditabenecontratto.data_vendita).toLocaleDateString("it-IT")
                      : "-",
                  sale_price_gross:
                    d.benecontratto?.[0]?.venditabenecontratto?.totale_lordo !== undefined
                      ? d.benecontratto[0].venditabenecontratto.totale_lordo + "€"
                      : "-",
                  sale_price_net:
                    d.benecontratto?.[0]?.venditabenecontratto?.totale_netto !== undefined
                      ? d.benecontratto[0].venditabenecontratto.totale_netto + "€"
                      : "-",
                  selling_notes:
                    d.benecontratto?.[0]?.venditabenecontratto
                      ?.selling_notes !== undefined
                      ? d.benecontratto[0].venditabenecontratto.selling_notes
                      : "-",
                  actions: d,
                  delete_action: d,
                  hasDetails: true,
                  hasDelete: true,
                };
              })
            : []
        }
        rowCount={beni?.count}
        onPaginationModelChange={setPaginationModel}
        columns={columns}
        errorMessage={errorMessage}
        hideError={hideError}
        filters={filters}
        sortModel={sortModel}
        onFilterChange={(newFilterModel) => setFilters(newFilterModel)}
        onSortChange={(newSortModel) => {
          newSortModel.forEach((item) => {
            if (item !== undefined) {
              setSortBy(item.field);
              setSortDirection(castToBackendSort(item.sort));
            }
          });

          setSortModel(newSortModel);
        }}
      />

      {/* Create */}
      {
        <BeneFormComponent
          initialData={
            { id_cliente: anagrafica_cliente_id } as BeneModelDetailed
          }
          open={enableCreate ? !fabVisible : false}
          toggleOpen={() => {}}
          onClose={() => alterFabVisibility(true)}
          beniPerCliente={
            anagrafica_cliente_id !== -1 ? beni?.count : undefined
          }
        />
      }

      {/* Delete */}
      <DeleteDialog
        open={deleteAlertOpen}
        onClose={toggleDeleteAlert}
        identifier={selectedRowElement?.id}
        onPositive={() => {
          toggleDeleteAlert();
          const x = {} as WithData;
          x.data = selectedRowElement?.id;
          deleteMutation.mutate(x);
        }}
        onNegative={toggleDeleteAlert}
      />
    </>
  );
};

export default BeneTableComponent;
